import { useState, useMemo, useEffect } from "react"
import { Flame, CheckCircle2, XCircle, ChevronRight, Trophy, Zap, Lock } from "lucide-react"
import { Sidebar } from "../components/ui/sidebar"
import ProtectedRoute from "../components/ProtectedRoute"
import { MICRO_COURSE_CATALOG as MICRO_COURSES } from "@/data/micro-courses"
import { goalEngine } from "@/hooks/useGoalEngine"
import api from "@/hooks/api/apiClient"
import config from "../../config"

// ─── Pull a deterministic daily question from all course questions ─────────────
function getDailyQuestion() {
  const allQuestions = []
  MICRO_COURSES.forEach((course) => {
    course.modules.forEach((mod) => {
      mod.questions.forEach((q) => {
        allQuestions.push({ ...q, courseTitle: course.title, moduleTitle: mod.title })
      })
    })
  })
  if (!allQuestions.length) return null
  // Deterministic by day — same question for everyone on the same date
  const dayIndex = Math.floor(Date.now() / 86400000)
  return allQuestions[dayIndex % allQuestions.length]
}

function todayKey() {
  return new Date().toISOString().slice(0, 10) // "2026-04-22"
}

function getStreak(history) {
  let streak = 0
  const today = new Date()
  for (let i = 0; i < 30; i++) {
    const d = new Date(today)
    d.setDate(today.getDate() - i)
    const key = d.toISOString().slice(0, 10)
    if (history[key] === "correct" || history[key] === "wrong") streak++
    else break
  }
  return streak
}

const XP_CORRECT = 25
const XP_WRONG = 5

export default function DailyChallengePage() {
  const question = useMemo(() => getDailyQuestion(), [])

  const [history, setHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem("daily-challenge-history") ?? "{}") } catch { return {} }
  })
  const [selected, setSelected] = useState(null)
  const [revealed, setRevealed] = useState(false)
  const [xpAwarded, setXpAwarded] = useState(false)

  const today = todayKey()
  const alreadyAnswered = !!history[today]
  const streak = getStreak(history)

  const handleAnswer = (optionId) => {
    if (alreadyAnswered) return
    setSelected(optionId)
    setRevealed(true)
    const correct = question.options.find(o => o.id === optionId)?.correct ?? false
    const newHistory = { ...history, [today]: correct ? "correct" : "wrong" }
    setHistory(newHistory)
    localStorage.setItem("daily-challenge-history", JSON.stringify(newHistory))

    // Fire goal engine so any "log_activity" goals progress
    if (!xpAwarded) {
      goalEngine.onActivityLogged()
      setXpAwarded(true)
    }

    // Persist to backend so history follows the user across devices
    if (config.appMode !== "prototype") {
      api.post("/students/me/daily-challenge", { date: today, correct }).catch(() => {})
    }
  }

  // Build a 7-day history bar
  const last7 = Array.from({ length: 7 }, (_, i) => {
    const d = new Date()
    d.setDate(d.getDate() - (6 - i))
    const key = d.toISOString().slice(0, 10)
    return { key, result: history[key] ?? null, isToday: key === today }
  })

  if (!question) return null

  const correctOption = question.options.find(o => o.correct)
  const chosenCorrect = selected ? question.options.find(o => o.id === selected)?.correct : null

  return (
    <ProtectedRoute>
      <div className="flex min-h-screen bg-slate-100">
        <Sidebar />
        <main className="flex-1 lg:ml-64 p-4 md:p-8 overflow-y-auto">
          <div className="max-w-2xl mx-auto space-y-6">

            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-black text-slate-900">Daily Challenge</h1>
                <p className="text-slate-500 text-sm mt-1">One question every day. Keep your streak alive.</p>
              </div>
              <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-2xl px-4 py-2">
                <Flame className="w-5 h-5 text-orange-500" />
                <span className="font-black text-orange-600 text-xl">{streak}</span>
                <span className="text-xs text-orange-400 font-semibold">day streak</span>
              </div>
            </div>

            {/* 7-day streak bar */}
            <div className="bg-white rounded-2xl border border-slate-200 p-4">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3">This week</p>
              <div className="flex justify-between gap-2">
                {last7.map(({ key, result, isToday }) => (
                  <div key={key} className="flex flex-col items-center gap-1.5 flex-1">
                    <div className={`w-full h-8 rounded-lg flex items-center justify-center transition-all ${
                      result === "correct" ? "bg-emerald-500" :
                      result === "wrong" ? "bg-red-400" :
                      isToday ? "bg-indigo-100 border-2 border-indigo-400 border-dashed" :
                      "bg-slate-100"
                    }`}>
                      {result === "correct" && <CheckCircle2 className="w-4 h-4 text-white" />}
                      {result === "wrong" && <XCircle className="w-4 h-4 text-white" />}
                      {!result && isToday && <Zap className="w-4 h-4 text-indigo-400" />}
                      {!result && !isToday && <Lock className="w-4 h-4 text-slate-300" />}
                    </div>
                    <span className="text-[10px] text-slate-400 font-medium">
                      {new Date(key + "T12:00:00").toLocaleDateString("en", { weekday: "short" })}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Question card */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="bg-linear-to-r from-indigo-600 to-violet-600 px-6 py-4">
                <p className="text-indigo-200 text-xs font-semibold uppercase tracking-wide">
                  {question.courseTitle} · {question.moduleTitle}
                </p>
                <p className="text-white font-bold text-lg mt-1 leading-snug">{question.text}</p>
              </div>

              <div className="p-6 space-y-3">
                {question.options.map((opt) => {
                  let style = "bg-slate-50 border-slate-200 text-slate-800 hover:bg-indigo-50 hover:border-indigo-300"
                  if (revealed) {
                    if (opt.correct) style = "bg-emerald-50 border-emerald-400 text-emerald-800"
                    else if (opt.id === selected && !opt.correct) style = "bg-red-50 border-red-400 text-red-800"
                    else style = "bg-slate-50 border-slate-200 text-slate-400"
                  }

                  return (
                    <button
                      key={opt.id}
                      onClick={() => handleAnswer(opt.id)}
                      disabled={alreadyAnswered}
                      className={`w-full text-left px-5 py-3.5 rounded-xl border-2 text-sm font-semibold transition-all flex items-center gap-3 ${style} disabled:cursor-default`}
                    >
                      <span className="w-6 h-6 rounded-full bg-white border border-current flex items-center justify-center text-xs font-black shrink-0">
                        {opt.id}
                      </span>
                      {opt.text}
                      {revealed && opt.correct && <CheckCircle2 className="w-4 h-4 text-emerald-600 ml-auto shrink-0" />}
                      {revealed && opt.id === selected && !opt.correct && <XCircle className="w-4 h-4 text-red-500 ml-auto shrink-0" />}
                    </button>
                  )
                })}
              </div>

              {/* Post-answer feedback */}
              {revealed && (
                <div className={`mx-6 mb-6 p-4 rounded-xl border ${chosenCorrect ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {chosenCorrect
                      ? <><CheckCircle2 className="w-4 h-4 text-emerald-600" /><span className="font-bold text-emerald-700 text-sm">Correct! +{XP_CORRECT} XP</span></>
                      : <><XCircle className="w-4 h-4 text-red-500" /><span className="font-bold text-red-700 text-sm">Not quite. +{XP_WRONG} XP for trying</span></>
                    }
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">{question.explanation}</p>
                </div>
              )}
            </div>

            {/* Already answered today */}
            {alreadyAnswered && !revealed && (
              <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center space-y-2">
                <Trophy className="w-10 h-10 text-amber-400 mx-auto" />
                <p className="font-bold text-slate-800">You've already answered today's challenge!</p>
                <p className="text-sm text-slate-500">Come back tomorrow to keep your streak going.</p>
                <p className="text-xs text-slate-400 mt-1">
                  Your answer: <span className={`font-bold ${history[today] === "correct" ? "text-emerald-600" : "text-red-500"}`}>
                    {history[today] === "correct" ? "Correct" : "Incorrect"}
                  </span>
                </p>
              </div>
            )}

            {/* XP info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl border border-slate-200 p-4 text-center">
                <p className="text-2xl font-black text-emerald-600">+{XP_CORRECT}</p>
                <p className="text-xs text-slate-500 mt-1 font-medium">XP for correct answer</p>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-4 text-center">
                <p className="text-2xl font-black text-indigo-600">+{XP_WRONG}</p>
                <p className="text-xs text-slate-500 mt-1 font-medium">XP for participating</p>
              </div>
            </div>

          </div>
        </main>
      </div>
    </ProtectedRoute>
  )
}
